/**
 * useCallbacksFeed — the booked-call reminders half of the overlay manager.
 *
 * The old CallbackReminderHost's semantics, unchanged: poll GET /api/crm/callbacks/
 * upcoming every 60s, re-derive the m10/m1 fire windows from each callback's
 * live scheduled_at (never a long setTimeout — laptop sleep would drift it),
 * fire each (callback, window) exactly once, prune the de-dupe set as bookings
 * leave the horizon so a genuine re-book can legitimately re-fire, and raise an
 * OS Notification with the same `cb-${win}:${id}` tag and body.
 *
 * Two differences: the evaluation rides the manager's 1s ticker instead of its
 * own 15s one (a window that opens between polls now pops within a second), and
 * the 60s auto-dismiss belongs to the queue, which only spends it while the card
 * is actually on screen. "Most imminent wins" still holds — m1 replaces a
 * lingering m10 rather than stacking a second card for the same patient.
 *
 * OUT-OF-OFFICE COVERAGE (docs/crm-out-of-office-coverage-plan.md). The feed is
 * no longer strictly "your own calls": /callbacks/upcoming widens to the calls
 * this viewer is HOLDING because their owner is out today, so an unattributed
 * pop-up would hand a recruiter a patient they have never spoken to with no way
 * to tell. Every covered row carries `coverage`, and it has to survive the trip
 * onto the card — both into the in-app body (a CoverageBadge) and into the
 * desktop Notification, which is often the only one of the two anybody reads.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { fireWindow } from './callbackWindow';
import type { ReminderWindow } from './callbackWindow';
import type { CoverageRef, UpcomingCallback } from '../../types';

const POLL_MS = 60_000;

export interface CallbackCard {
  /** `${callbackId}:${window}` — also the queue item id and the de-dupe key. */
  id: string;
  callbackId: string;
  contactId: string;
  window: ReminderWindow;
  firstName: string | null;
  scheduledAt: string;
  /**
   * Set ONLY when this call is on the viewer's screen because its assignee is
   * out of office today. Ownership is NOT transferred — its presence is the
   * whole signal, and the card renders a CoverageBadge from it.
   */
  coverage?: CoverageRef | null;
}

export function timeLabel(scheduledAt: string): string {
  return new Date(scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * "Dana's lead" — the one-clause attribution appended to the DESKTOP
 * notification body, where there is no room for a chip and no CoverageBadge to
 * render. Null for the viewer's own calls, so their own reminders read exactly
 * as they always have.
 *
 * A name ending in "s" takes the bare apostrophe ("Chris' lead"); anything the
 * server could not name at all degrades to "a teammate's lead" rather than to
 * "undefined's".
 */
export function coverageNote(coverage: CoverageRef | null | undefined): string | null {
  if (!coverage) return null;
  const first = (coverage.owner_name || '').trim().split(/\s+/)[0];
  if (!first) return "a teammate's lead";
  return /s$/i.test(first) ? `${first}' lead` : `${first}'s lead`;
}

export interface CallbacksFeed {
  card: CallbackCard | null;
  dismiss: (id: string) => void;
  /** Dismiss the card, then navigate to the lead it's for. */
  open: (c: CallbackCard) => void;
}

export function useCallbacksFeed(nowMs: number): CallbacksFeed {
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState<UpcomingCallback[]>([]);
  const [card, setCard] = useState<CallbackCard | null>(null);
  const firedKeyRef = useRef<Set<string>>(new Set());

  // Poll the slim feed every 60s. A missed poll is harmless — swallow errors.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/api/crm/callbacks/upcoming');
        if (!cancelled) setUpcoming(res.data?.callbacks || []);
      } catch {
        /* a missed poll is harmless; do not surface a banner */
      }
    };
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Prune de-dupe keys for callbacks no longer in the horizon, so a genuine
  // re-book within the window can legitimately re-fire.
  useEffect(() => {
    const live = new Set<string>();
    for (const cb of upcoming) {
      live.add(`${cb.id}:m10`);
      live.add(`${cb.id}:m1`);
    }
    for (const key of Array.from(firedKeyRef.current)) {
      if (!live.has(key)) firedKeyRef.current.delete(key);
    }
  }, [upcoming]);

  // Re-derive the fire windows from live scheduled_at on every tick of the
  // manager's clock. NEVER cache a fire time / schedule a long setTimeout.
  useEffect(() => {
    for (const cb of upcoming) {
      const minutesUntil = (new Date(cb.scheduled_at).getTime() - nowMs) / 60000;
      const win = fireWindow(minutesUntil);
      if (!win) continue;
      const key = `${cb.id}:${win}`;
      if (firedKeyRef.current.has(key)) continue;
      firedKeyRef.current.add(key);
      // Most-imminent wins — m1 supersedes a lingering m10 card.
      setCard({
        id: key,
        callbackId: cb.id,
        contactId: cb.contact_id,
        window: win,
        firstName: cb.first_name,
        scheduledAt: cb.scheduled_at,
        coverage: cb.coverage ?? null,
      });
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const note = coverageNote(cb.coverage);
          new Notification('Upcoming call', {
            // The attribution rides the BODY, not the title: the title is what
            // the OS truncates first, and "whose call is this?" must not be the
            // part that gets cut.
            body: `${cb.first_name || 'A patient'} — ${timeLabel(cb.scheduled_at)}${note ? ` · ${note}` : ''}`,
            tag: `cb-${win}:${cb.id}`, // OS collapses dupes across tabs
          });
        }
      } catch {
        /* denied/unsupported — the in-app card still fires */
      }
    }
  }, [upcoming, nowMs]);

  const dismiss = useCallback(
    (id: string) => setCard((prev) => (prev && prev.id === id ? null : prev)),
    [],
  );

  const open = useCallback((c: CallbackCard) => {
    dismiss(c.id);
    navigate(`/crm/contacts/${c.contactId}`);
  }, [dismiss, navigate]);

  return { card, dismiss, open };
}

export default useCallbacksFeed;
